import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileText,
  Download,
  RefreshCw,
  AlertCircle,
  Check,
  X,
  Plus,
  Loader2,
  Video,
  MessageSquare,
  Heart,
  Eye,
  Bookmark,
  Share2,
  Repeat,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  listAccounts,
  verifyAccount,
  addAccount as apiAddAccount,
  deleteAccount as apiDeleteAccount,
  checkAccount,
  extractContent,
  getMonitorConfig,
  saveMonitorConfig,
  getAccountAnalysis,
  type MonitorConfig,
} from '@/api/client';
import { showToast } from '@/components/ui/toast';

// ─── 类型 ───────────────────────────────────────────────────────

type AccountInfo = {
  nickname: string;
  avatar: string;
  followers: number;
  aweme_count: number;
  signature: string;
};

type Account = {
  id: string;
  platform: string;
  uid: string;
  info: AccountInfo;
  video_count: number;
  added_at: string;
  last_check_at: string;
  analysis?: { keywords: { word: string; count: number }[]; tags: { tag: string; count: number }[] } | null;
};

type Video = {
  id: number;
  video_id: string;
  title: string;
  description: string;
  platform: string;
  account_uid: string;
  account_name: string;
  cover_url: string;
  play_count: number;
  digg_count: number;
  comment_count: number;
  share_count: number;
  collect_count: number;
  duration: number;
  media_type: string;
  create_time: string;
  deleted?: number;
  transcript: string | null;
  transcribe_status: string | null;
  download_status: string | null;
  added_at: string;
};

// ─── 常量 ───────────────────────────────────────────────────────

const PLATFORM_CONFIG: Record<string, { label: string; color: string; badgeBg: string }> = {
  douyin: { label: '抖音', color: 'bg-sky-500', badgeBg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  xiaohongshu: { label: '小红书', color: 'bg-pink-500', badgeBg: 'bg-pink-500/10 text-pink-600 dark:text-pink-400' },
  wechat: { label: '视频号', color: 'bg-emerald-500', badgeBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  finder: { label: '视频号', color: 'bg-emerald-500', badgeBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
};

function platformBadge(platform: string) {
  const cfg = PLATFORM_CONFIG[platform];
  if (!cfg) return <Badge variant="outline">{platform}</Badge>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.badgeBg}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.color}`} />
      {cfg.label}
    </span>
  );
}

function formatNumber(n: number | null | undefined): string {
  if (n == null || n <= 0) return '—';
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatDuration(s: number | null | undefined): string {
  if (!s) return '—';
  let secs = s;
  if (secs > 10000) secs = Math.floor(secs / 1000); // 毫秒防御
  const m = Math.floor(secs / 60);
  const sec = Math.floor(secs % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

type VideoStat = { icon: React.ComponentType<{ className?: string }>; label: string; value: number };

/** 按平台返回应展示的数据指标（小红书/视频号没有播放量，显示各自的互动指标） */
function platformStats(video: Video): VideoStat[] {
  const p = video.platform;
  if (p === 'xiaohongshu') {
    return [
      { icon: Heart, label: '点赞', value: video.digg_count },
      { icon: Bookmark, label: '收藏', value: video.collect_count },
      { icon: MessageSquare, label: '评论', value: video.comment_count },
      { icon: Share2, label: '分享', value: video.share_count },
    ];
  }
  if (p === 'wechat' || p === 'finder') {
    return [
      { icon: Heart, label: '点赞', value: video.digg_count },
      { icon: MessageSquare, label: '评论', value: video.comment_count },
      { icon: Repeat, label: '转发', value: video.share_count },
    ];
  }
  return [
    { icon: Eye, label: '播放', value: video.play_count },
    { icon: Heart, label: '点赞', value: video.digg_count },
    { icon: MessageSquare, label: '评论', value: video.comment_count },
  ];
}

// ─── 主页面 ─────────────────────────────────────────────────────

export default function MonitorPage() {
  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [videos, setVideos] = React.useState<Record<string, Video[]>>({});
  const [videosLoading, setVideosLoading] = React.useState<Record<string, boolean>>({});
  // 手动监测状态（按账号 id 独立）
  const [checkingAccounts, setCheckingAccounts] = React.useState<Record<string, boolean>>({});
  const [checkMsg, setCheckMsg] = React.useState<{ accountId: string; ok: boolean; text: string } | null>(null);

  // 添加账号弹窗
  const [showAdd, setShowAdd] = React.useState(false);
  const [addPlatform, setAddPlatform] = React.useState('douyin');
  const [addUrl, setAddUrl] = React.useState('');
  const [verifyResult, setVerifyResult] = React.useState<{ uid: string; info: AccountInfo } | null>(null);
  const [verifyLoading, setVerifyLoading] = React.useState(false);
  const [verifyError, setVerifyError] = React.useState('');

  // 视频预览弹窗
  const [previewVideo, setPreviewVideo] = React.useState<Video | null>(null);
  const [extracting, setExtracting] = React.useState(false);

  // 定时监控配置
  const [monitorCfg, setMonitorCfg] = React.useState<MonitorConfig>({
    enabled: false,
    times_per_day: 1,
    time_points: ['12:00'],
  });
  const [monitorLoading, setMonitorLoading] = React.useState(false);
  const [monitorSaved, setMonitorSaved] = React.useState(false);
  // 执行时间编辑态
  const [timeEditing, setTimeEditing] = React.useState(false);
  const [timeDraft, setTimeDraft] = React.useState<string[]>(['12:00']);
  // 定时监控模块红字提醒（如：请先添加账号）
  const [monitorNotice, setMonitorNotice] = React.useState('');
  // 无账号开启缓冲：先视觉开启，短暂后自动关闭
  const openingPendingRef = React.useRef(false);

  // 最新配置镜像：避免快速连续操作时闭包读到过期 state
  const monitorCfgRef = React.useRef(monitorCfg);
  React.useEffect(() => {
    monitorCfgRef.current = monitorCfg;
  }, [monitorCfg]);

  const loadAccounts = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listAccounts();
      setAccounts(data.accounts || []);
      // 逐个账号补分析结果（展开区/账号行展示用）
      const accs = data.accounts || [];
      const withAnalysis = await Promise.all(
        accs.map(async (a: any) => {
          try {
            const an = await getAccountAnalysis(a.id);
            return { ...a, analysis: { keywords: an.keywords || [], tags: an.tags || [] } };
          } catch {
            return { ...a, analysis: null };
          }
        })
      );
      setAccounts(withAnalysis);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 静默刷新账号列表（不触发整列表骨架屏，用于监测轮询）
  const reloadAccountsSilently = React.useCallback(async () => {
    try {
      const data = await listAccounts();
      setAccounts(data.accounts || []);
    } catch { /* 保持现有数据 */ }
  }, []);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // ─── 加载监控配置 ──────────────────────────────────────────────

  React.useEffect(() => {
    (async () => {
      try {
        const cfg = await getMonitorConfig();
        setMonitorCfg(cfg);
      } catch { /* ignore */ }
    })();
  }, []);

  // ─── 生成时间点 ────────────────────────────────────────────────

  const generateTimePoints = React.useCallback((times: number): string[] => {
    if (times <= 1) return ['12:00'];
    if (times === 2) return ['09:00', '21:00'];
    // 3 次
    return ['09:00', '15:00', '21:00'];
  }, []);

  // 统一保存入口：基于最新配置镜像构造 next，避免闭包读到过期 state
  const persistMonitor = React.useCallback(async (next: MonitorConfig) => {
    monitorCfgRef.current = next;
    setMonitorCfg(next);
    setMonitorLoading(true);
    setMonitorSaved(false);
    try {
      await saveMonitorConfig(next);
      setMonitorSaved(true);
      setTimeout(() => setMonitorSaved(false), 2000);
    } catch (err) {
      console.error('保存监控配置失败', err);
      showToast('监控设置保存失败，请重试');
    } finally {
      setMonitorLoading(false);
    }
  }, []);

  // 开关：切换即生效（立即保存）；无账号时先视觉开启 → 缓冲后自动关闭 + 模块下方红字提醒
  const handleToggleEnable = React.useCallback(() => {
    setMonitorNotice('');
    const next = !monitorCfgRef.current.enabled;
    if (!next) {
      // 关闭：立即保存
      openingPendingRef.current = false;
      void persistMonitor({ ...monitorCfgRef.current, enabled: false });
      return;
    }
    // 开启：有账号立即生效
    if (accounts.length > 0) {
      void persistMonitor({ ...monitorCfgRef.current, enabled: true });
      return;
    }
    // 无账号：先视觉开启，短暂缓冲后自动关闭，红字提醒
    if (openingPendingRef.current) return;
    openingPendingRef.current = true;
    const visual = { ...monitorCfgRef.current, enabled: true };
    monitorCfgRef.current = visual;
    setMonitorCfg(visual);
    window.setTimeout(() => {
      openingPendingRef.current = false;
      const cur = monitorCfgRef.current;
      const restored = { ...cur, enabled: false };
      monitorCfgRef.current = restored;
      setMonitorCfg(restored);
      void persistMonitor(restored);
      setMonitorNotice('请先添加监控账号，再开启定时监控');
    }, 700);
  }, [accounts.length, persistMonitor]);

  // 次数滑块：滑动时更新本地 state + 实时成本提示，松手/键盘确认时保存
  const handleTimesChange = React.useCallback((times: number) => {
    const prev = monitorCfgRef.current;
    const next = { ...prev, times_per_day: times, time_points: generateTimePoints(times) };
    monitorCfgRef.current = next;
    setMonitorCfg(next);
  }, [generateTimePoints]);

  const handleTimesCommit = React.useCallback(() => {
    const cur = monitorCfgRef.current;
    void persistMonitor({ ...cur, time_points: generateTimePoints(cur.times_per_day) });
  }, [persistMonitor, generateTimePoints]);

  // ─── 执行时间编辑（默认自动生成，点「修改」才就地编辑） ──────────

  const handleStartEditTime = React.useCallback(() => {
    setTimeDraft([...monitorCfgRef.current.time_points]);
    setTimeEditing(true);
  }, []);

  const handleCancelEditTime = React.useCallback(() => {
    setTimeEditing(false);
  }, []);

  const handleSaveTime = React.useCallback(() => {
    const cleaned = timeDraft
      .map((t) => t.trim())
      .filter((t) => /^\d{2}:\d{2}$/.test(t));
    if (cleaned.length === 0) {
      showToast('请至少保留一个执行时间');
      return;
    }
    setTimeEditing(false);
    void persistMonitor({ ...monitorCfgRef.current, time_points: cleaned });
  }, [timeDraft, persistMonitor]);

  const handleAddTime = React.useCallback(() => {
    setTimeDraft((prev) => [...prev, '12:00']);
  }, []);

  const handleRemoveTime = React.useCallback((idx: number) => {
    setTimeDraft((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleTimeDraftChange = React.useCallback((idx: number, val: string) => {
    setTimeDraft((prev) => prev.map((t, i) => (i === idx ? val : t)));
  }, []);

  // 预估每日消耗成本：次数 ×（视频号账号数×video单价 + 其他账号数×other单价）
  const costPerDay = React.useMemo(() => {
    const rates = monitorCfg.credit_rates || { video: 0, other: 0 };
    const videoCnt = accounts.filter((a) => a.platform === 'wechat').length;
    const otherCnt = accounts.length - videoCnt;
    const perRun = videoCnt * (rates.video || 0) + otherCnt * (rates.other || 0);
    return perRun * monitorCfg.times_per_day;
  }, [accounts, monitorCfg.credit_rates, monitorCfg.times_per_day]);

  // ─── 筛选（搜索已移除，展示全部账号）────────────────────────

  const filtered = accounts;

  // ─── 展开账号 ────────────────────────────────────────────────

  const loadAccountVideos = React.useCallback(async (accountId: string, acc: Account) => {
    try {
      const res = await fetch(`/api/media/library?platform=${acc.platform}&sort=create_time&order=desc&page_size=20`);
      const data = await res.json();
      setVideos((prev) => ({ ...prev, [accountId]: (data.items || []).filter((v: any) => v.account_uid === acc.uid) }));
    } catch {
      setVideos((prev) => ({ ...prev, [accountId]: [] }));
    }
  }, []);

  const toggleExpand = React.useCallback(async (accountId: string) => {
    if (expandedId === accountId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(accountId);
    if (!videos[accountId]) {
      setVideosLoading((prev) => ({ ...prev, [accountId]: true }));
      const acc = accounts.find((a) => a.id === accountId);
      if (acc) await loadAccountVideos(accountId, acc);
      setVideosLoading((prev) => ({ ...prev, [accountId]: false }));
    }
  }, [expandedId, accounts, videos, loadAccountVideos]);

  // ─── 添加账号 ────────────────────────────────────────────────

  const handleVerify = React.useCallback(async () => {
    if (!addUrl) { setVerifyError('请输入链接或 UID'); return; }
    setVerifyLoading(true);
    setVerifyError('');
    setVerifyResult(null);
    try {
      const data = await verifyAccount(addPlatform, addUrl);
      setVerifyResult({ uid: data.uid, info: data.info });
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : '查询失败');
    } finally {
      setVerifyLoading(false);
    }
  }, [addPlatform, addUrl]);

  const handleAdd = React.useCallback(async () => {
    if (!verifyResult) return;
    try {
      await apiAddAccount(addPlatform, verifyResult.uid, verifyResult.info);
      setShowAdd(false);
      setAddUrl('');
      setVerifyResult(null);
      setAddPlatform('douyin');
      setMonitorNotice('');
      loadAccounts();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : '添加失败');
    }
  }, [verifyResult, addPlatform, loadAccounts, setMonitorNotice]);

  // 删除确认弹窗状态（替代原生 confirm）
  const [deleteTarget, setDeleteTarget] = React.useState<Account | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const handleDelete = React.useCallback((account: Account) => {
    setDeleteTarget(account);
  }, []);

  const confirmDelete = React.useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDeleteAccount(deleteTarget.id);
      setAccounts((prev) => prev.filter((a) => String(a.id) !== String(deleteTarget.id)));
      // 清理该账号关联的视频/展开/监测状态
      setVideos((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setVideosLoading((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setCheckingAccounts((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setExpandedId((prev) => (prev === deleteTarget.id ? null : prev));
      setDeleteTarget(null);
      showToast('账号已删除', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '删除失败';
      showToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  // ─── 手动监测单个账号 ────────────────────────────────────────

  const handleCheckAccount = React.useCallback(async (accountId: string) => {
    if (checkingAccounts[accountId]) return; // 该账号已在监测中，防重复触发
    setCheckingAccounts((prev) => ({ ...prev, [accountId]: true }));
    setCheckMsg(null);
    try {
      await checkAccount(accountId);
      showToast('已触发监测，数据更新中', 'success');
      // 后台线程异步入库，轮询刷新该账号的视频数据与账号列表
      const acc = accounts.find((a) => a.id === accountId);
      let attempt = 0;
      const refresh = async () => {
        if (acc) await loadAccountVideos(accountId, acc);
        await reloadAccountsSilently();
      };
      await refresh();
      // 后台入库通常 1-4 秒，轮询几次覆盖更新窗口
      const timer = setInterval(async () => {
        attempt += 1;
        await refresh();
        if (attempt >= 3) {
          clearInterval(timer);
          setCheckingAccounts((prev) => ({ ...prev, [accountId]: false }));
          setCheckMsg({ accountId, ok: true, text: '数据已更新' });
          showToast('数据已更新', 'success');
          setTimeout(() => setCheckMsg((prev) => (prev?.accountId === accountId ? null : prev)), 3000);
        }
      }, 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '监测失败';
      // 积分不足已由全局 toast 提示，避免重复
      if (!msg.includes('积分不足')) showToast(msg, 'error');
      setCheckMsg({ accountId, ok: false, text: msg });
      setCheckingAccounts((prev) => ({ ...prev, [accountId]: false }));
    }
  }, [checkingAccounts, accounts, loadAccountVideos, reloadAccountsSilently]);

  // ─── 提取文案 ────────────────────────────────────────────────

  const handleExtract = React.useCallback(async (video: Video) => {
    setExtracting(true);
    try {
      await extractContent(video.video_id, video.platform);
      // 更新本地状态
      setVideos((prev) => {
        const list = { ...prev };
        for (const key of Object.keys(list)) {
          list[key] = list[key].map((v) =>
            v.video_id === video.video_id ? { ...v, download_status: 'downloading' } : v
          );
        }
        return list;
      });
    } catch (err) {
      console.error('提取失败', err);
    } finally {
      setExtracting(false);
    }
  }, []);

  // ─── 渲染 ────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">对标监控</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          抖音、小红书、视频号账号数据监控
        </p>
      </div>

      {/* 错误提示（账号加载失败，全宽置顶） */}
      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {error}
          <Button variant="outline" size="sm" className="ml-auto" onClick={loadAccounts}>重试</Button>
        </div>
      )}

      {/* 左右布局：账号列表 2/3 + 定时监控 1/3 */}
      <div className="grid items-start gap-6 xl:grid-cols-3">
        {/* 账号列表（左 2/3） */}
        <div className="min-w-0 xl:col-span-2">
      {/* 账号列表 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle>账号列表</CardTitle>
              <CardDescription>{filtered.length} 个账号</CardDescription>
            </div>
            {/* 刷新 + 添加账号（账号列表右上角） */}
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" className="h-8" onClick={loadAccounts} title="刷新">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" className="h-8" onClick={() => { setShowAdd(true); setVerifyResult(null); setAddUrl(''); setVerifyError(''); }}>
                <Plus className="h-3.5 w-3.5" />
                添加账号
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-lg" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="mb-3 h-8 w-8" />
              <p className="text-sm">暂无监控账号</p>
              <p className="mt-1 text-xs">点击「添加账号」开始监控</p>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((account) => (
                <React.Fragment key={account.id}>
                  {/* 账号行 */}
                  <div
                    className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-muted/30 cursor-pointer"
                    onClick={() => toggleExpand(account.id)}
                  >
                    {/* 展开图标 */}
                    <button className="flex-shrink-0 text-muted-foreground hover:text-foreground">
                      {expandedId === account.id ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>

                    {/* 头像 */}
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                      {account.info?.avatar ? (
                        <img src={account.info.avatar} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold text-muted-foreground">
                          {(account.info?.nickname || '?')[0]}
                        </span>
                      )}
                    </div>

                    {/* 信息 */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{account.info?.nickname || account.uid}</span>
                        {platformBadge(account.platform)}
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>粉丝 {formatNumber(account.info?.followers)}</span>
                        <span>视频 {account.video_count || 0}</span>
                        <span>UID: {account.uid.slice(0, 12)}</span>
                      </div>
                      {/* 账号内容关键词（TOP 5） */}
                      {account.analysis?.keywords && account.analysis.keywords.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {account.analysis.keywords.slice(0, 5).map((k) => (
                            <span
                              key={k.word}
                              className="inline-flex items-center rounded bg-accent/50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                            >
                              {k.word}
                              <span className="ml-0.5 text-[9px] opacity-60">{k.count}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 操作 */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {checkMsg?.accountId === account.id && (
                        <span className={`text-xs mr-1 ${checkMsg.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                          {checkMsg.text}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={(e) => { e.stopPropagation(); handleCheckAccount(account.id); }}
                        disabled={checkingAccounts[account.id]}
                        title="手动监测此账号（扣除积分）"
                      >
                        {checkingAccounts[account.id] ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        监测
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDelete(account); }}
                        title="删除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* 展开的视频列表 */}
                  {expandedId === account.id && (
                    <div className="border-t bg-muted/20 px-6 py-4">
                      {/* 账号分析：内容关键词 + 话题标签 */}
                      {account.analysis && (account.analysis.keywords.length > 0 || account.analysis.tags.length > 0) && (
                        <div className="mb-4 space-y-3 rounded-lg border bg-background p-4">
                          <div>
                            <div className="mb-1.5 text-xs font-semibold text-muted-foreground">内容关键词</div>
                            <div className="flex flex-wrap gap-1.5">
                              {account.analysis.keywords.slice(0, 15).map((k) => (
                                <span
                                  key={k.word}
                                  className="inline-flex items-center gap-1 rounded-full bg-accent/40 px-2 py-0.5 text-[11px] font-medium"
                                >
                                  {k.word}
                                  <span className="text-[10px] text-muted-foreground">{k.count}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                          {account.analysis.tags.length > 0 && (
                            <div>
                              <div className="mb-1.5 text-xs font-semibold text-muted-foreground">内容标签</div>
                              <div className="flex flex-wrap gap-1.5">
                                {account.analysis.tags.slice(0, 20).map((t) => (
                                  <span
                                    key={t.tag}
                                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
                                  >
                                    #{t.tag}
                                    <span className="text-[10px] opacity-70">{t.count}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {videosLoading[account.id] ? (
                        <div className="space-y-2">
                          <Skeleton className="h-12 rounded" />
                          <Skeleton className="h-12 rounded" />
                        </div>
                      ) : videos[account.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {videos[account.id].map((video) => (
                            <div
                              key={video.id}
                              className="flex items-center gap-3 rounded-lg border bg-background p-3 transition-colors hover:bg-accent/30"
                            >
                              {/* 封面缩略图 */}
                              <div
                                className="relative flex h-16 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted cursor-pointer"
                                onClick={() => setPreviewVideo(video)}
                              >
                                {video.cover_url ? (
                                  <img src={video.cover_url} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <Video className="h-6 w-6 text-muted-foreground" />
                                )}
                                {video.media_type === 'video' && (
                                  <div className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[10px] text-white">
                                    {formatDuration(video.duration)}
                                  </div>
                                )}
                                {video.deleted ? (
                                  <span className="absolute right-1 top-1 rounded bg-gray-600/80 px-1 py-0.5 text-[10px] font-medium text-white">用户已删除</span>
                                ) : null}
                              </div>

                              {/* 信息 */}
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium">
                                  {video.title || video.description || '无标题'}
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                  {platformStats(video).map((s, i) => (
                                    <span key={i} className="inline-flex items-center gap-1" title={s.label}>
                                      <s.icon className="h-3 w-3" />{formatNumber(s.value)}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* 操作按钮 */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-xs"
                                  onClick={() => setPreviewVideo(video)}
                                  title="预览"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-xs"
                                  onClick={() => handleExtract(video)}
                                  disabled={extracting}
                                  title="提取文案"
                                >
                                  {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                                  提取
                                </Button>
                                {video.transcript ? (
                                  <Badge variant="success" className="text-[10px]">已转录</Badge>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground">转录中/待转录</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                          <FileText className="mr-2 h-4 w-4" />
                          暂无视频数据，请先提取内容
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </div>

        {/* 定时监控（右 1/3） */}
        <div className="min-w-0 xl:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">定时监控</CardTitle>
                  <CardDescription>打开开关即生效，按设置时间自动检测账号更新</CardDescription>
                </div>
                {monitorSaved && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">✓ 已保存</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 启用开关（切换即保存生效；无账号先开再自动关） */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={monitorCfg.enabled}
                    onClick={handleToggleEnable}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                      monitorCfg.enabled ? 'bg-primary' : 'bg-input'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow ring-0 transition-transform duration-200 ${
                        monitorCfg.enabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm font-medium">
                    {monitorCfg.enabled ? '已启用' : '已关闭'}
                  </span>
                </label>

                {/* 每天监控次数：3 档滑块（松手即保存）+ 圆点档位标记 */}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">每天监控</span>
                    <span className="text-sm font-medium">{monitorCfg.times_per_day} 次</span>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={1}
                      value={monitorCfg.times_per_day}
                      onChange={(e) => handleTimesChange(Number(e.target.value))}
                      onPointerUp={handleTimesCommit}
                      onKeyUp={handleTimesCommit}
                      className="w-full accent-primary"
                      aria-label="每天监控次数"
                    />
                    {/* 3 档圆点：当前档高亮 */}
                    <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">
                      {[1, 2, 3].map((v) => (
                        <span
                          key={v}
                          className={`h-2.5 w-2.5 rounded-full ${
                            v === monitorCfg.times_per_day
                              ? 'bg-primary'
                              : 'bg-muted-foreground/40'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    预估消耗成本：{costPerDay} 积分/天
                  </p>
                </div>

                {/* 执行时间：默认自动生成，点「修改」就地编辑 */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">执行时间：</span>
                  {timeEditing ? (
                    <>
                      {timeDraft.map((tp, i) => (
                        <div key={i} className="flex items-center gap-0.5">
                          <input
                            type="time"
                            value={tp}
                            onChange={(e) => handleTimeDraftChange(i, e.target.value)}
                            className="h-7 w-24 rounded-md border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveTime(i)}
                            disabled={timeDraft.length <= 1}
                            className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-destructive disabled:opacity-30"
                            title="删除该时间点"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddTime}
                        className="inline-flex items-center gap-0.5 rounded-md border border-input px-1.5 py-0.5 text-xs hover:bg-accent/50"
                      >
                        <Plus className="h-3 w-3" /> 添加
                      </button>
                      <Button
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={handleSaveTime}
                        disabled={monitorLoading}
                      >
                        {monitorLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                        保存设置
                      </Button>
                      <button
                        type="button"
                        onClick={handleCancelEditTime}
                        className="inline-flex items-center rounded-md border border-input px-1.5 py-0.5 text-xs hover:bg-accent/50"
                      >
                        取消
                      </button>
                    </>
                  ) : (
                    <>
                      {monitorCfg.time_points.map((tp, i) => (
                        <React.Fragment key={tp}>
                          <span className="inline-flex items-center rounded-md border bg-accent/30 px-1.5 py-0.5 text-xs font-medium">
                            {tp}
                          </span>
                          {i < monitorCfg.time_points.length - 1 && (
                            <span className="text-xs text-muted-foreground">/</span>
                          )}
                        </React.Fragment>
                      ))}
                      <button
                        type="button"
                        onClick={handleStartEditTime}
                        className="ml-1 inline-flex items-center rounded-md border border-input px-1.5 py-0.5 text-xs hover:bg-accent/50"
                      >
                        修改
                      </button>
                    </>
                  )}
                </div>

                {/* 模块下方红字提醒 */}
                {monitorNotice && (
                  <p className="text-xs text-destructive">{monitorNotice}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ─── 添加账号弹窗 ─── */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAdd(false)}>
          <div className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight">添加监控账号</h3>
            <p className="mt-1 text-sm text-muted-foreground">输入账号链接或 UID，确认后添加到监控列表</p>

            <div className="mt-4 space-y-4">
              {/* 平台选择 */}
              <div>
                <label className="text-sm font-medium">平台</label>
                <div className="mt-1.5 flex gap-2">
                  {[
                    { key: 'douyin', label: '抖音', dot: 'bg-sky-500' },
                    { key: 'xiaohongshu', label: '小红书', dot: 'bg-pink-500' },
                    { key: 'wechat', label: '视频号', dot: 'bg-emerald-500' },
                  ].map((p) => (
                    <Button
                      key={p.key}
                      variant={addPlatform === p.key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setAddPlatform(p.key); setVerifyResult(null); }}
                    >
                      <span className={`mr-1 h-2 w-2 rounded-full ${p.dot}`} />
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* 链接输入 */}
              <div>
                <label className="text-sm font-medium">链接或 UID</label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    placeholder="粘贴分享链接或输入 UID…"
                    value={addUrl}
                    onChange={(e) => setAddUrl(e.target.value)}
                  />
                  <Button onClick={handleVerify} disabled={verifyLoading}>
                    {verifyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '查询'}
                  </Button>
                </div>
              </div>

              {verifyError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {verifyError}
                </div>
              )}

              {/* 查询结果 */}
              {verifyResult && (
                <div className="rounded-lg border bg-accent/30 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                      {verifyResult.info?.avatar ? (
                        <img src={verifyResult.info.avatar} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Users className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{verifyResult.info?.nickname || '未知'}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {verifyResult.info?.followers != null && `${formatNumber(verifyResult.info.followers)} 粉丝 · `}
                        {verifyResult.info?.aweme_count != null && `${verifyResult.info.aweme_count} 视频`}
                      </div>
                      {verifyResult.info?.signature && (
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{verifyResult.info.signature}</div>
                      )}
                    </div>
                  </div>
                  <Button className="mt-3 w-full" onClick={handleAdd}>
                    <Check className="h-4 w-4" />
                    确认添加
                  </Button>
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={() => setShowAdd(false)}>
                取消
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 视频预览弹窗 ─── */}
      {previewVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewVideo(null)}>
          <div className="flex w-full max-w-2xl flex-col rounded-xl border bg-background shadow-lg" onClick={(e) => e.stopPropagation()}>
            {/* 标题栏 */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="font-semibold">视频预览</h3>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreviewVideo(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* 内容 */}
            <div className="max-h-[70vh] overflow-y-auto p-6 space-y-4">
              {/* 封面 */}
              {previewVideo.cover_url && (
                <div className="overflow-hidden rounded-lg border">
                  <img src={previewVideo.cover_url} alt="" className="w-full object-cover" />
                </div>
              )}

              {/* 信息 */}
              <div>
                <h4 className="font-medium">{previewVideo.title || previewVideo.description || '无标题'}</h4>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
                  {platformBadge(previewVideo.platform)}
                  {platformStats(previewVideo).map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1" title={s.label}><s.icon className="h-3.5 w-3.5" />{formatNumber(s.value)}</span>
                  ))}
                </div>
              </div>

              {/* 描述 */}
              {previewVideo.description && (
                <div>
                  <h5 className="mb-1 text-sm font-medium">文案</h5>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{previewVideo.description}</p>
                </div>
              )}

              {/* 转录状态 */}
              {previewVideo.transcript && (
                <div>
                  <h5 className="mb-1 text-sm font-medium">逐字稿</h5>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{previewVideo.transcript}</p>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExtract(previewVideo)}
                  disabled={extracting}
                >
                  {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {previewVideo.download_status === 'downloaded' ? '已下载' : '提取文案'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ─── 删除确认弹窗 ─── */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { if (!deleting) setDeleteTarget(null); }}
          >
            <motion.div
              className="w-full max-w-sm overflow-hidden rounded-xl border bg-background shadow-2xl"
              initial={{ opacity: 0, scale: 0.92, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={{ type: 'spring', duration: 0.28, bounce: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部：危险色条 + 图标 */}
              <div className="flex items-start gap-3 border-b bg-destructive/5 px-5 py-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <Trash2 className="h-5 w-5 text-destructive" />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="text-base font-semibold tracking-tight">删除监控账号</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    删除后不再监控该账号，已下载视频与提取内容保留在内容库
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 -mr-1 -mt-1"
                  disabled={deleting}
                  onClick={() => setDeleteTarget(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* 账号信息 */}
              <div className="px-5 py-4">
                <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
                    {deleteTarget.info?.avatar ? (
                      <img src={deleteTarget.info.avatar} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Users className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{deleteTarget.info?.nickname || deleteTarget.uid}</span>
                      {platformBadge(deleteTarget.platform)}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      粉丝 {formatNumber(deleteTarget.info?.followers)} · 视频 {deleteTarget.video_count || 0}
                    </div>
                  </div>
                </div>

                <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  此操作不可撤销，确定要删除「{deleteTarget.info?.nickname || deleteTarget.uid}」吗？
                </p>
              </div>

              {/* 操作 */}
              <div className="flex gap-2 border-t bg-muted/20 px-5 py-3.5">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={deleting}
                  onClick={() => setDeleteTarget(null)}
                >
                  取消
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={deleting}
                  onClick={confirmDelete}
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {deleting ? '删除中…' : '确认删除'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}